import React, { useState } from 'react';
import ProductPicker from 'components/ProductPicker.tsx';
import AmountInput from '../purchase/AmountInput.tsx';
import ProductImage from '../purchase/ProductImage.tsx';
import { Product } from 'utils/grocyApi.ts';
import { useQueue } from 'contexts/QueueContext.tsx';
import { useStockQuery } from 'hooks/useQueries.ts';
import './ManualProductPicker.css';

interface ManualProductPickerProps {
  onClose: () => void;
}

const ManualProductPicker: React.FC<ManualProductPickerProps> = ({ onClose }) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [amount, setAmount] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { addConsumeOperation } = useQueue();
  const { data: stock = [] } = useStockQuery();

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleConsume = () => {
    if (selectedProduct) {
      // Queue one consume operation with the requested amount
      addConsumeOperation(selectedProduct.id, amount);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedProduct(null);
    setAmount(1);
    setSearchTerm('');
    onClose();
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    setAmount(1);
  };

  const handleSetToAll = () => {
    setAmount(currentStockAmount);
  };

  const handleAmountChange = (newAmount: number) => {
    // Limit the amount to the current stock level and keep it positive
    const limitedAmount = Math.max(1, Math.min(newAmount, currentStockAmount));
    setAmount(limitedAmount);
  };

  // Get current stock amount for selected product
  const getCurrentStockAmount = () => {
    if (!selectedProduct || !stock.length) return 0;
    
    const stockItem = stock.find((item: { product_id: number; amount: number }) => 
      item.product_id === selectedProduct.id
    );
    
    return stockItem ? stockItem.amount : 0;
  };

  const currentStockAmount = getCurrentStockAmount();



  return (
    <div className="manual-product-picker">
      <div className="manual-product-picker__content">
        <div className="manual-product-picker__header">
          <h3>Pick Product to Consume</h3>
          <button
            type="button"
            onClick={handleCancel}
            className="manual-product-picker__close-btn"
          >
            ×
          </button>
        </div>

                 {!selectedProduct && (
           <div className="manual-product-picker__search-section">
             <ProductPicker
               searchTerm={searchTerm}
               onSearchTermChange={setSearchTerm}
               onSelect={handleProductSelect}
               autoFocus
               showList={true}
               onlyInStock={true}
             />
           </div>
         )}

                 {selectedProduct && (
           <div className="manual-product-picker__product-section">
             <div className="manual-product-picker__selected-product">
               <div className="manual-product-picker__selected-product-header">
                 <h4>Selected Product:</h4>
                 <button
                   type="button"
                   onClick={handleClearProduct}
                   className="manual-product-picker__clear-product-btn"
                   title="Clear selection"
                 >
                   ×
                 </button>
               </div>
              <div className="manual-product-picker__product-info">
                <div className="manual-product-picker__product-row">
                  <ProductImage 
                    product={selectedProduct} 
                    size="small" 
                    className="manual-product-picker__product-image"
                  />
                                     <div className="manual-product-picker__product-details">
                     <span className="manual-product-picker__product-name">
                       {selectedProduct.name}
                     </span>
                     {selectedProduct.description && (
                       <span className="manual-product-picker__product-description">
                         {selectedProduct.description}
                       </span>
                     )}
                     <span className="manual-product-picker__stock-info">
                       {currentStockAmount > 0 ? (
                         <span className="manual-product-picker__stock-amount">
                           {currentStockAmount} in stock
                         </span>
                       ) : (
                         <span className="manual-product-picker__stock-empty">
                           Out of stock
                         </span>
                       )}
                     </span>
                   </div>
                </div>
              </div>
            </div>

                         <div className="manual-product-picker__amount-section">
               <div className="manual-product-picker__amount-label">Amount:</div>
               <div className="manual-product-picker__amount-row">
                 <AmountInput
                   amount={amount}
                   onChangeAmount={handleAmountChange}
                   max={currentStockAmount}
                 />
                 <button
                   type="button"
                   onClick={handleSetToAll}
                   className="manual-product-picker__all-btn"
                   disabled={currentStockAmount <= 0}
                 >
                   All
                 </button>
               </div>
             </div>

            <div className="manual-product-picker__actions">
              <button
                type="button"
                onClick={handleConsume}
                className="manual-product-picker__consume-btn"
                disabled={currentStockAmount <= 0 || amount <= 0}
              >
                Consume {amount} {amount === 1 ? 'item' : 'items'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="manual-product-picker__cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualProductPicker;
